const DEFAULT_SERVER_TIMEZONE = "America/New_York";

type ServerTimeContextState = {
  serverNow: string | null;
  serverToday: string | null;
  serverTimezone: string;
  serverNowCapturedAt: number | null;
};

type ServerTimeContextInput = {
  serverNow?: string | null;
  serverToday?: string | null;
  serverTimezone?: string | null;
};

let serverTimeContext: ServerTimeContextState = {
  serverNow: null,
  serverToday: null,
  serverTimezone: DEFAULT_SERVER_TIMEZONE,
  serverNowCapturedAt: null,
};

const SERVER_PARTS_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: DEFAULT_SERVER_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const toServerTimezoneFormatter = (timeZone: string) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

export type CalendarDate = {
  year: number;
  month: number;
  day: number;
};

const normalizeServerTimezone = (timeZone?: string | null): string => {
  if (!timeZone) return DEFAULT_SERVER_TIMEZONE;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return DEFAULT_SERVER_TIMEZONE;
  }
};

export const setServerTimeContext = (next: ServerTimeContextInput): void => {
  const nextServerNow = next.serverNow ?? serverTimeContext.serverNow;
  serverTimeContext = {
    serverNow: nextServerNow,
    serverToday: next.serverToday ?? serverTimeContext.serverToday,
    serverTimezone: normalizeServerTimezone(next.serverTimezone ?? serverTimeContext.serverTimezone),
    serverNowCapturedAt:
      next.serverNow !== undefined ? (nextServerNow ? Date.now() : null) : serverTimeContext.serverNowCapturedAt,
  };
};

export const setServerTimezone = (timeZone?: string | null): void => {
  serverTimeContext.serverTimezone = normalizeServerTimezone(timeZone);
};

export const getServerTimezone = (): string => serverTimeContext.serverTimezone;

export const getServerNow = (): string | null => serverTimeContext.serverNow;

export const getServerToday = (): string | null => serverTimeContext.serverToday;

const extractParts = (date: Date, timeZone: string): Record<string, string> => {
  const formatter =
    timeZone === DEFAULT_SERVER_TIMEZONE
      ? SERVER_PARTS_FORMATTER
      : toServerTimezoneFormatter(timeZone);

  return formatter.formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    if (part.type !== "literal") {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});
};

export const getCurrentServerInstant = (): Date => {
  const serverNow = getServerNow();
  const serverNowCapturedAt = serverTimeContext.serverNowCapturedAt;

  if (serverNow && serverNowCapturedAt) {
    const parsed = new Date(serverNow);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getTime() + (Date.now() - serverNowCapturedAt));
    }
  }

  return new Date();
};

const calendarDateIn = (instant: Date, timeZone: string): CalendarDate => {
  const parts = extractParts(instant, timeZone);
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
};

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
const calendarDayDiff = (from: CalendarDate, to: CalendarDate): number =>
  Math.round(
    (Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) /
      86_400_000,
  );

/**
 * The server's current calendar date.
 *
 * `server_today` is captured once, when the session is fetched (page load) — it must not stay
 * frozen: an app left open past the server's midnight would keep querying YESTERDAY as "today"
 * (header alert badge, Alerts "Today", …) while rolling windows (`getCurrentServerInstant`) move
 * on. So when the session also gave `server_now`, advance `server_today` by the server-timezone
 * midnights crossed since it was captured. (`server_today` stays the anchor — it is authoritative
 * for the agency's day; only the rollover is derived.)
 * Same rule as the dashboard MFE (transitos dispatcher-mfe shared/utils/serverTimeContext.ts).
 */
export const getServerCalendarDate = (): CalendarDate => {
  const knownToday = getServerToday();
  const match = knownToday?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const anchor: CalendarDate = {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
    };
    const capturedNow = getServerNow();
    const captured = capturedNow ? new Date(capturedNow) : null;
    if (captured && !Number.isNaN(captured.getTime()) && serverTimeContext.serverNowCapturedAt) {
      const tz = getServerTimezone();
      const elapsedDays = calendarDayDiff(
        calendarDateIn(captured, tz),
        calendarDateIn(getCurrentServerInstant(), tz),
      );
      return elapsedDays === 0 ? anchor : addServerCalendarDays(anchor, elapsedDays);
    }
    return anchor;
  }

  return calendarDateIn(getCurrentServerInstant(), getServerTimezone());
};

export const toUtcNoon = ({ year, month, day }: CalendarDate): Date =>
  new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

const fromUtcDate = (date: Date): CalendarDate => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth() + 1,
  day: date.getUTCDate(),
});

export const addServerCalendarDays = (date: CalendarDate, days: number): CalendarDate => {
  const utcDate = toUtcNoon(date);
  utcDate.setUTCDate(utcDate.getUTCDate() + days);
  return fromUtcDate(utcDate);
};

export const getServerWeekStart = (date: CalendarDate): CalendarDate => {
  const utcDate = toUtcNoon(date);
  const dayOfWeek = utcDate.getUTCDay();
  const daysFromMonday = (dayOfWeek + 6) % 7;
  return addServerCalendarDays(date, -daysFromMonday);
};

export const getServerWeekEnd = (date: CalendarDate): CalendarDate =>
  addServerCalendarDays(getServerWeekStart(date), 6);

/**
 * Returns the start of the calendar week that contains `date`, anchored to
 * the given `weekStartDay` (0 = Sunday, 1 = Monday).
 *
 * Examples for Wednesday 2026-08-19:
 *   weekStartDay = 1 (Monday) → 2026-08-17  (Mon)
 *   weekStartDay = 0 (Sunday) → 2026-08-16  (Sun)
 */
export const getServerWeekStartForDow = (
  date: CalendarDate,
  weekStartDay: 0 | 1,
): CalendarDate => {
  const utcDate = toUtcNoon(date);
  // getUTCDay(): 0=Sun, 1=Mon, …, 6=Sat
  const dayOfWeek = utcDate.getUTCDay();
  // How many days back to the desired week-start day?
  // e.g. weekStartDay=1 (Mon): dayOfWeek=0(Sun) → 6 days back; dayOfWeek=3(Wed) → 2 days back
  const daysBack = (dayOfWeek - weekStartDay + 7) % 7;
  return addServerCalendarDays(date, -daysBack);
};

const pad = (value: number): string => String(value).padStart(2, "0");

export const formatServerDateTime = (
  date: CalendarDate,
  hours: number,
  minutes: number,
  seconds: number,
): string =>
  `${date.year}-${pad(date.month)}-${pad(date.day)}T${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

export const formatServerDate = (date: CalendarDate): string =>
  `${date.year}-${pad(date.month)}-${pad(date.day)}`;

/**
 * Maps a YYYY-MM-DD date string to the legacy `q.range_default_enum` value
 * used by RunDetails#readScheduleFiltersFromUrl to pre-filter the schedule tab.
 *
 * Returns:
 *   "today"     — dateStr matches the current server date
 *   "yesterday" — dateStr matches yesterday
 *   "tomorrow"  — dateStr matches tomorrow
 *   "time"      — any other date (caller must also supply q.range_default_date)
 */
export const resolveRunScheduleTimePeriod = (dateStr: string): string => {
  const today     = formatServerDate(getServerCalendarDate());
  const tomorrow  = formatServerDate(addServerCalendarDays(getServerCalendarDate(), 1));
  const yesterday = formatServerDate(addServerCalendarDays(getServerCalendarDate(), -1));

  if (dateStr === today)     return 'today';
  if (dateStr === tomorrow)  return 'tomorrow';
  if (dateStr === yesterday) return 'yesterday';
  return 'time';
};

export const formatServerStartOfDay = (date: CalendarDate): string =>
  formatServerDateTime(date, 0, 0, 0);

export const formatServerEndOfDay = (date: CalendarDate): string =>
  formatServerDateTime(date, 23, 59, 59);

/**
 * Formats an absolute `Date` instant as `YYYY-MM-DDTHH:MM:SS` in the
 * server's timezone, with NO offset suffix. The dashboard's K3 queries
 * send timestamps without offsets (matching `formatServerStartOfDay` /
 * `formatServerEndOfDay` and the existing `getTodayTimeRange()`
 * convention), so any "now − X hours" / "now − X minutes" boundary
 * shipped to K3 must be expressed in server-local wall-clock time
 * regardless of where the dispatcher's browser is.
 *
 * Use this when you have an absolute instant (e.g. server-now,
 * server-now − 1 h) and need to express it as a K3 query parameter.
 * For day-aligned boundaries use `formatServerStartOfDay/EndOfDay`
 * instead.
 */
export const formatServerInstant = (instant: Date): string => {
  const parts = extractParts(instant, getServerTimezone());
  const hour = parts.hour === "24" ? "00" : parts.hour ?? "00";
  return (
    `${parts.year}-${parts.month}-${parts.day}` +
    `T${hour}:${parts.minute}:${parts.second}`
  );
};

/** Cached short time-zone-name formatters keyed by timezone (e.g. "EDT"). */
const zoneNameFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getZoneNameFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = zoneNameFormatterCache.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" });
  zoneNameFormatterCache.set(timeZone, formatter);
  return formatter;
};

/**
 * Formats an instant as a file-name timestamp `YYYYMMDD_HHMM_<TZ>` in `timeZone`,
 * e.g. `20261022_1530_EDT`. The single implementation behind every export file
 * name stamp (formatServerFileTimestamp, and dateUtils' Eastern-time helpers).
 */
export const formatFileTimestamp = (instant: Date, timeZone: string): string => {
  const parts = extractParts(instant, timeZone);
  const hour = parts.hour === "24" ? "00" : parts.hour ?? "00";
  const zoneName =
    getZoneNameFormatter(timeZone).formatToParts(instant).find((part) => part.type === "timeZoneName")?.value ?? "";
  return `${parts.year}${parts.month}${parts.day}_${hour}${parts.minute}_${zoneName}`;
};

/**
 * File-name timestamp in the server's timezone — drt_ui Export::Registry#new_export_name.
 * Defaults to the current server instant, so names never depend on the browser's clock or zone.
 */
export const formatServerFileTimestamp = (instant: Date = getCurrentServerInstant()): string =>
  formatFileTimestamp(instant, getServerTimezone());

// ─── HH:MM helper ─────────────────────────────────────────────────────────────

/** Cached formatters keyed by timezone — avoids re-allocating per call. */
const hhmmFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getHhMmFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = hhmmFormatterCache.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  hhmmFormatterCache.set(timeZone, formatter);
  return formatter;
};

/**
 * Returns the current server wall-clock time as a "HH:MM" 24-hour string
 * expressed in the server's configured timezone.
 *
 * Uses a cached `Intl.DateTimeFormat` instance — safe to call on every
 * render tick without performance impact.
 */
export const getServerNowHHMM = (): string => {
  const parts = getHhMmFormatter(getServerTimezone()).formatToParts(
    getCurrentServerInstant(),
  );
  const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
  return `${hour}:${minute}`;
};

/**
 * Parse a naive ISO-8601 datetime string that was produced by the K3 server
 * in the server's local timezone (e.g. `"2026-08-17T08:49:08"` from
 * `America/New_York`) into the correct UTC `Date`.
 *
 * `Date.parse` / `new Date` treat a no-offset ISO string as **UTC**, which
 * inflates age calculations by the server's UTC offset (e.g. 4 hours for EDT).
 * This function corrects that by:
 *   1. Parsing the naive string as-if-UTC (fast, no regex).
 *   2. Re-expressing that UTC instant in the server's timezone via `Intl`.
 *   3. Computing the delta between the Intl wall-clock and the naive input.
 *   4. Subtracting the delta to recover the true UTC epoch.
 *
 * Returns `NaN` (as `Date`) if the input is malformed, preserving the same
 * guard contract as `Date.parse`.
 */
export const parseServerLocalIso = (naiveIso: string): Date => {
  // Step 1 — treat the naive string as UTC to get a candidate instant.
  const candidateMs = Date.parse(naiveIso);
  if (Number.isNaN(candidateMs)) return new Date(NaN);

  const candidate = new Date(candidateMs);
  const tz = getServerTimezone();

  // Step 2 — ask Intl what the server's wall-clock reads for this candidate.
  const parts = extractParts(candidate, tz);
  // Reconstruct an ISO string from the Intl parts and parse as UTC to get
  // the "what UTC epoch has this server-local wall-clock reading" value.
  const intlWallMs = Date.parse(
    `${parts.year}-${parts.month}-${parts.day}T${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}:${parts.second}`,
  );
  if (Number.isNaN(intlWallMs)) return new Date(NaN);

  // Step 3 & 4 — the offset between what Intl shows and the naive input is
  // the server timezone's UTC offset at this instant. Subtract it.
  const offsetMs = intlWallMs - candidateMs; // e.g. EDT: intlWall is 4h behind → offsetMs = -14400000
  return new Date(candidateMs - offsetMs);
};
